import { useQuery } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import { useAsset } from "../api/assetlib";
import type { CastRow } from "../api/cast";
import type { AssetDetail, Dressing } from "../api/types";
import { Viewer, ViewerSlot } from "../assets/inspector";
import { useDressingDetails } from "../assets/useDressingDetails";
import { CAST } from "../copy/cast";
import { parseSidecar } from "../film/manifest";
import { Button, Dialog, Skeleton } from "../ui";
import { ErrorLine } from "../ui/ErrorLine";
import { CastFilmContext, objectOf } from "./castContext";
import "./cast-object.css";

function AssetObject({ asset }: { asset: NonNullable<CastRow["asset"]> }) {
  const query = useAsset(asset.kind, asset.id, asset.version);
  if (query.isError)
    return (
      <>
        <ErrorLine error={query.error} />
        <Button type="button" onClick={() => void query.refetch()}>
          {CAST.retry}
        </Button>
      </>
    );
  if (!query.data) return <Skeleton height={440} />;
  return <ObjectModel detail={query.data} onExpired={query.onExpired} />;
}

function ObjectModel({
  detail,
  onExpired,
}: {
  detail: AssetDetail;
  onExpired(): void;
}) {
  const shape = detail.shape ?? detail;
  const dressing = useMemo<Dressing>(
    () => ({
      materials: detail.kind === "variant" ? (detail.meta.materials ?? {}) : {},
      skins: detail.kind === "variant" ? (detail.meta.skins ?? {}) : {},
      colors_hex:
        detail.kind === "variant" ? (detail.meta.colors_hex ?? {}) : {},
      poses: {},
    }),
    [detail],
  );
  const details = useDressingDetails(detail, dressing);
  const subject = useMemo(
    () => ({
      kind: "shape" as const,
      detail: {
        ...shape,
        files: {
          ...shape.files,
          "proxy.glb":
            shape.files["proxy.glb"] ??
            shape.files["web_model.glb"] ??
            shape.files["model.glb"],
        },
      },
    }),
    [shape],
  );
  return (
    <ViewerSlot>
      <Viewer
        subject={subject}
        dressing={dressing}
        details={details}
        allowPan
        onExpired={onExpired}
      />
    </ViewerSlot>
  );
}

/** Stand-ins still have a real model in their scene's bake. Extract only that object,
 * keeping its baked appearance; never substitute the room or the scene's product. */
function BakedObject({ row }: { row: CastRow }) {
  const manifest = useContext(CastFilmContext);
  const scene = manifest?.scenes.find(
    (s) => row.scenes.includes(s.id) && s.ready && s.glb_url && s.sidecar_url,
  );
  const query = useQuery({
    queryKey: ["cast-object-sidecar", scene?.sidecar_url, row.key],
    enabled: !!scene?.sidecar_url,
    queryFn: async ({ signal }) => {
      const response = await fetch(scene!.sidecar_url!, { signal });
      if (!response.ok) throw new Error(CAST.viewUnavailable);
      const sidecar = parseSidecar(await response.json());
      const id = objectOf(row.key);
      const placed = sidecar.objects.find((o) => o.id === id);
      const person = sidecar.people.find((p) => p.id === id || p.crowd === id);
      const figure =
        person && sidecar.figures.find((f) => f.ref === person.figure);
      const node = placed?.node ?? figure?.node;
      if (!node) throw new Error(CAST.viewUnavailable);
      return node;
    },
  });
  const subject = useMemo(
    () => ({
      kind: "shape" as const,
      node: query.data,
      detail: {
        id: row.key,
        version: 1,
        meta: { name: row.name, slots: [], parts: [] },
        files: { "proxy.glb": scene?.glb_url ?? "" },
        viewer: "full" as const,
      },
    }),
    [query.data, row.key, row.name, scene?.glb_url],
  );
  if (!scene || query.isError)
    return <p role="status">{CAST.viewUnavailable}</p>;
  if (!query.data) return <Skeleton height={440} />;
  return (
    <ViewerSlot>
      <Viewer subject={subject} allowPan />
    </ViewerSlot>
  );
}

export function CastObjectDialog({
  row,
  onClose,
}: {
  row: CastRow;
  onClose(): void;
}) {
  return (
    <Dialog open onClose={onClose} title={row.name} size="large">
      <div className="cast-object-preview">
        <p className="m-0 text-sm text-slate-600">{CAST.viewHelp}</p>
        {row.asset ? (
          <AssetObject asset={row.asset} />
        ) : (
          <BakedObject row={row} />
        )}
      </div>
    </Dialog>
  );
}
